<template>
    <div v-if="readonly" class="date-time-read">
        <div v-for="row in dateRows" :key="row.label" class="date-time-row">
            <span class="label">{{ `${row.label}:` }}</span>
            <span class="value">{{ row.value }}</span>
        </div>
    </div>
    <div v-else class="date-time-edit">
        <div>
            <v-label>{{ labels.startPublish }}</v-label>
            <div class="date-from">
                <div>
                    <v-menu
                        v-model="startPublish.menu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        min-width="290px"
                        offset-y
                        transition="scale-transition"
                    >
                        <template #activator="{ props: menuProps }">
                            <v-text-field
                                v-model="startPublish.date"
                                data-id="DateTextField"
                                readonly
                                v-bind="menuProps"
                                variant="outlined"
                                append-inner-icon="mdi-calendar"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="startPublishPicker"
                            data-id="DatePicker"
                            show-adjacent-months
                            @update:model-value="updateStartPublishTime"
                        ></v-date-picker>
                    </v-menu>
                </div>
                <div>
                    <v-text-field
                        v-model="startPublish.time"
                        type="time"
                        data-id="TimeTextField"
                        variant="outlined"
                        @update:model-value="updateStartPublishTime"
                    ></v-text-field>
                </div>
            </div>
            <v-label>{{ labels.validFrom }}</v-label>
            <div class="valid-from">
                <div>
                    <v-menu
                        v-model="validFrom.menu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        min-width="290px"
                        offset-y
                        transition="scale-transition"
                    >
                        <template #activator="{ props: menuProps }">
                            <v-text-field
                                v-model="validFrom.date"
                                data-id="DateTextField"
                                readonly
                                v-bind="menuProps"
                                variant="outlined"
                                append-inner-icon="mdi-calendar"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="validFromPicker"
                            data-id="DatePicker"
                            show-adjacent-months
                            :allowed-dates="allowedValidFromDates"
                            @update:model-value="updateValidFromTime"
                        ></v-date-picker>
                    </v-menu>
                </div>
                <div>
                    <v-text-field
                        v-model="validFrom.time"
                        type="time"
                        data-id="TimeTextField"
                        variant="outlined"
                        @update:model-value="updateValidFromTime"
                    ></v-text-field>
                </div>
            </div>
            <v-label>{{ labels.endPublish }}</v-label>
            <div class="date-to">
                <div>
                    <v-menu
                        v-model="endPublish.menu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        min-width="290px"
                        offset-y
                        transition="scale-transition"
                    >
                        <template #activator="{ props: menuProps }">
                            <v-text-field
                                v-model="endPublish.date"
                                data-id="DateTextField"
                                readonly
                                v-bind="menuProps"
                                variant="outlined"
                                append-inner-icon="mdi-calendar"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="endPublishPicker"
                            :allowed-dates="allowedPublishDates"
                            data-id="DatePicker"
                            show-adjacent-months
                            @update:model-value="updateEndPublishTime"
                        ></v-date-picker>
                    </v-menu>
                </div>
                <div>
                    <v-text-field
                        v-model="endPublish.time"
                        type="time"
                        data-id="TimeTextField"
                        variant="outlined"
                        @update:model-value="updateEndPublishTime"
                    ></v-text-field>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { ToLocalDate, FormatDateISO, DateTimeToUTC, DateToUTC } from '@/shared/DateFormatHelpers';

const { t } = useI18n();

interface Props {
    startDate?: Date;
    endDate?: Date;
    validFromDate?: Date;
    readonly?: boolean;
}
const props = withDefaults(defineProps<Props>(), { readonly: false });

const emit = defineEmits<{
    (e: 'updateStartPublishTime', dateTime: Date);
    (e: 'updateEndPublishTime', dateTime: Date);
    (e: 'updateValidFromTime', dateTime: Date);
}>();

const labels = computed(() => ({
    startPublish: t('messages.resources.publishFrom', 'Publisert fra'),
    validFrom: t('messages.resources.listStartPublish', 'Gjeldende fra'),
    endPublish: t('messages.resources.listEndPublish', 'Gjeldende til')
}));

const dateRows = computed(() => [
    { label: labels.value.startPublish, value: ToLocalDate(props.startDate) },
    { label: labels.value.validFrom, value: ToLocalDate(props.validFromDate) },
    { label: labels.value.endPublish, value: ToLocalDate(props.endDate) }
]);

const validFrom = ref({
    menu: false,
    menuTime: false,
    date: props.validFromDate ? ToLocalDate(props.validFromDate, 'ISOdate') : '',
    time: props.validFromDate ? ToLocalDate(props.validFromDate, 'time') : ''
});

const validFromPicker = computed<Date>({
    get: () => {
        return new Date(validFrom.value.date);
    },
    set: (value: Date) => {
        const d = value;
        validFrom.value.date = FormatDateISO(d);
    }
});

const startPublish = ref({
    menu: false,
    menuTime: false,
    date: props.startDate ? ToLocalDate(props.startDate, 'ISOdate') : '',
    time: props.startDate ? ToLocalDate(props.startDate, 'time') : ''
});

const startPublishPicker = computed<Date>({
    get: () => {
        return new Date(startPublish.value.date);
    },
    set: (value: Date) => {
        const d = value;
        startPublish.value.date = FormatDateISO(d);
    }
});

const endPublish = ref({
    menu: false,
    menuTime: false,
    date: props.endDate ? ToLocalDate(props.endDate, 'ISOdate') : '',
    time: props.endDate ? ToLocalDate(props.endDate, 'time') : ''
});

const endPublishPicker = computed<Date>({
    get: () => {
        return new Date(endPublish.value.date);
    },
    set: (value: Date) => {
        const d = value;
        endPublish.value.date = FormatDateISO(d);
    }
});

function allowedPublishDates(val: any) {
    return DateToUTC(val) >= DateToUTC(startPublish.value.date);
}

function allowedValidFromDates(val: any) {
    return DateToUTC(val) <= DateToUTC(endPublish.value.date);
}

function ensureValidEndStartTime(startTime: Date, endTime: Date) {
    // prevent possibility to have end publish time earlier than start publish time
    const isEndBeforeStart = startTime > endTime;

    if (isEndBeforeStart) {
        endPublish.value = {
            ...endPublish.value,
            time: startPublish.value.time,
            date: startPublish.value.date
        };
        emit('updateEndPublishTime', DateTimeToUTC(endPublish.value.date, endPublish.value.time));
    }
}

function ensureValidValidFromTime(validFromTime: Date, endTime: Date) {
    const isValidFromTime = validFromTime < endTime;

    if (!isValidFromTime) {
        const minuteInMilliseconds = 60000;
        const validFromDateTime = new Date(props.endDate!.getTime() - minuteInMilliseconds);
        const time = ToLocalDate(validFromDateTime, 'time');

        validFrom.value = {
            ...validFrom.value,
            time: time,
            date: endPublish.value.date
        };
        emit('updateValidFromTime', DateTimeToUTC(validFrom.value.date, validFrom.value.time));
    }
}

function updateStartPublishTime() {
    const newDateTime = DateTimeToUTC(startPublish.value.date, startPublish.value.time);
    emit('updateStartPublishTime', newDateTime);

    ensureValidEndStartTime(newDateTime, DateTimeToUTC(endPublish.value.date, endPublish.value.time));
}

function updateEndPublishTime() {
    const newDateTime = DateTimeToUTC(endPublish.value.date, endPublish.value.time);
    emit('updateEndPublishTime', newDateTime);

    ensureValidEndStartTime(DateTimeToUTC(startPublish.value.date, startPublish.value.time), newDateTime);
}

function updateValidFromTime() {
    const validFromDateTime = DateTimeToUTC(validFrom.value.date, validFrom.value.time);
    emit('updateValidFromTime', validFromDateTime);

    ensureValidValidFromTime(validFromDateTime, DateTimeToUTC(endPublish.value.date, endPublish.value.time));
}
</script>

<style lang="scss" scoped>
@use '@/shared/variables.scss' as *;

.date-time-read {
    font-size: 14px;

    .date-time-row {
        display: flex;
        justify-content: space-between;

        .label {
            font-weight: bold;
        }
    }
}

.valid-from,
.date-from,
.date-to {
    display: grid;
    grid-template-columns: calc(65% - 12px) calc(35% - 12px);
    column-gap: 24px;
}

.date-time-edit {
    :deep(input) {
        padding: 4px 16px;
        min-height: 40px;
    }

    :deep(.v-field) {
        background-color: $white;
    }

    :deep(.v-text-field .v-input__details) {
        display: none;
    }

    .v-label {
        font-size: 12px;
        margin-bottom: 8px;
        color: $black;
    }

    .date-from,
    .valid-from {
        margin-bottom: 12px;
    }
}
</style>
